import type { ColumnFilter, Row } from "@tanstack/react-table";
import type { TFunction } from "react-i18next";

import { Button, Modal, TableActionForSelection } from "src/components";
import { useModal } from "src/modal";
import { Color } from "src/theme";
import { CustomerCategoryEnum, CustomerStatusEnum, ExpandTableCustomerType } from "src/types";
import { isString, SPECIAL_PRICES_FORM_MODAL_WIDTH } from "src/utils";
import { SpecialPriceForm } from "../../../containers/specialPriceForm";
import { ALL_SPECIAL_PRICES_FORM_MODAL } from "../../constants";

export const overrideRowProps = (row: Row<ExpandTableCustomerType>): Record<string, unknown> => {
    if (row.original.status === CustomerStatusEnum.Inactive) {
        return { $backgroundColor: Color.supportGraphite50 };
    }
    if (row.original.status === CustomerStatusEnum.Blocked) {
        return { $backgroundColor: Color.neutral300 };
    }
    if (row.original.category === CustomerCategoryEnum.Payer) {
        return { $backgroundColor: Color.neutral50, $hoveredColor: Color.supportNavy50 };
    }
    return { $backgroundColor: Color.white };
};

export const createActionsForSelection =
    (t: TFunction) => (isAllRowSelected: boolean, selectedRows: ExpandTableCustomerType[], filters: ColumnFilter[]) => {
        const [isFormModalOpened, openFormModal, closeFormModal] = useModal(ALL_SPECIAL_PRICES_FORM_MODAL);

        return (
            <TableActionForSelection>
                <Modal
                    title={t("specialPrices.modal.createTitle")}
                    opened={isFormModalOpened}
                    onClose={closeFormModal}
                    maxWidth={SPECIAL_PRICES_FORM_MODAL_WIDTH}
                >
                    <SpecialPriceForm
                        customerId={null}
                        closeModal={closeFormModal}
                        shipTosForBulkCreate={selectedRows}
                        isAllRowsSelected={isAllRowSelected}
                        filters={convertFiltersToMatchPayerFilterDtoProperties(filters)}
                    />
                </Modal>
                <Button disabled={!selectedRows.length && !isAllRowSelected} onClick={openFormModal}>
                    {t("specialPrices.createNewBulk")}
                </Button>
            </TableActionForSelection>
        );
    };

const convertFiltersToMatchPayerFilterDtoProperties = (filters: ColumnFilter[]) => {
    const branch = filters.find((filter) => filter.id === "branch")?.value as string[] | undefined;
    const branchFixed = isString(branch) ? [Number(branch)] : branch?.map((id: string) => Number(id));

    const updatedFilters = filters.filter((filter) => filter.id !== "branch") ?? [];

    return branch ? [...updatedFilters, { id: "branchIds", value: branchFixed }] : filters;
};
